import type { MiceBudgetSituation, MiceBudgetView } from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { Button, Dot } from '@web/ui/core';
import type { BudgetLineState } from '../hooks/use_mice_budget';
import { formatMoney } from './need_format';

/** Cidades de saída que cabem no resumo; o resto fica na planilha. */
const MAX_ORIGINS = 5;

/**
 * Na linha de status: "Gerar orçamento" ou "Atualizar orçamento" e, durante a cotação, o progresso
 * ao lado. O botão segue clicável cotando: o operador sempre tem como pedir de novo.
 */
export function BudgetControl(props: Readonly<BudgetLineState>): React.JSX.Element {
  return (
    <span className="flex items-center gap-3">
      {props.quoting ? (
        <span className="flex items-center gap-1.5">
          <Dot tone="green" pulse />
          {t('mice.budget.quoting', { done: props.quoting.done, total: props.quoting.total })}
        </span>
      ) : null}
      <Button size="sm" variant="outline" disabled={props.generating} onClick={props.onGenerate}>
        {t(props.requested ? 'mice.budget.refresh' : 'mice.budget.generate')}
      </Button>
    </span>
  );
}

const countOf = (budget: MiceBudgetView, situation: MiceBudgetSituation): number =>
  budget.rows.filter((r) => r.situation === situation).length;

function Figure({ label, value }: Readonly<{ label: string; value: string }>): React.JSX.Element {
  return (
    <div className="flex flex-col">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

interface BudgetSectionProps {
  budget: MiceBudgetView;
  onDownload: () => void;
}

/** O orçamento no painel Resumo: total, saving, por cidade de saída, quem ficou de fora e o arquivo. */
export function BudgetSection({ budget, onDownload }: Readonly<BudgetSectionProps>) {
  const pending = countOf(budget, 'cadastro_pending');
  const noFlight = countOf(budget, 'no_solution');
  const excluded = countOf(budget, 'excluded');
  // Ainda cotando: o total soma só o que já foi cotado.
  const partial = budget.quoting.done < budget.quoting.total;
  return (
    <div className="flex flex-col gap-3 text-meta text-foreground-default">
      <div className="flex gap-6">
        <Figure
          label={t(partial ? 'mice.budget.totalPartial' : 'mice.budget.total')}
          value={budget.total ? formatMoney(budget.total) : '—'}
        />
        {budget.savings ? (
          <Figure label={t('mice.budget.savings')} value={formatMoney(budget.savings)} />
        ) : null}
      </div>
      {budget.byOrigin.length > 0 ? (
        <ul className="flex flex-col gap-1">
          {budget.byOrigin.slice(0, MAX_ORIGINS).map((o) => (
            <li key={o.origin} className="flex justify-between gap-2">
              <span>{t('mice.budget.origin', { origin: o.origin, count: o.travelers })}</span>
              <span className="text-foreground">
                {formatMoney({ amount: o.amount, currency: o.currency })}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {pending > 0 || noFlight > 0 || excluded > 0 ? (
        <div className="flex flex-col gap-1 text-tone-amber-ink">
          {pending > 0 ? <span>{t('mice.budget.cadastroPending', { count: pending })}</span> : null}
          {noFlight > 0 ? <span>{t('mice.budget.noSolution', { count: noFlight })}</span> : null}
          {excluded > 0 ? <span>{t('mice.budget.excluded', { count: excluded })}</span> : null}
        </div>
      ) : null}
      <span className="text-muted-foreground">{t('mice.budget.notice')}</span>
      <div>
        <Button size="sm" variant="outline" onClick={onDownload}>
          {t('mice.budget.download')}
        </Button>
      </div>
    </div>
  );
}
